import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Account } from 'types/account';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useHasAccess } from 'hooks/useHasAccess';

export type UseMembersParams = {
  account_ids?: string[];
  emails?: string[];
  names?: string[];
};

type UseMembersOptions = UseQueryOptions<OffsetPaginated<Account>, APIError> & {
  params?: UseMembersParams & OffsetPaginationParams;
};

/** List members of an organization */
export function useMembers({
  queryKey = ['members'],
  params,
  enabled,
  ...options
}: UseMembersOptions = {}) {
  const { applicationID, client } = useAPI();
  const { data: organization } = useCurrentOrganization();

  const organizationId = organization?.id;

  const [hasAccess] = useHasAccess();
  const queryParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      if (!organizationId) {
        return Promise.reject(new Error('No organization id provided.'));
      }

      const { data } = await client.get<OffsetPaginated<Account>>(
        `/internal/orgs/${organizationId}/members`,
        { params: queryParams }
      );
      return data;
    },
    {
      enabled:
        enabled !== undefined
          ? enabled &&
            Boolean(organizationId) &&
            hasAccess('organization_settings')
          : Boolean(organizationId) && hasAccess('organization_settings'),
      ...options,
    }
  );
}
